import "maplibre-gl/dist/maplibre-gl.css"
import { useMemo } from "react"

import { layers, namedFlavor } from "@protomaps/basemaps"
import { useQuery } from "@tanstack/react-query"
import Map, { FullscreenControl, type MapRef, type StyleSpecification } from "react-map-gl/maplibre"

import { useTheme } from "@/lib/useTheme"

import { getTripData } from "../../api/getTripData.ts"
import { getTruckLocations } from "../../api/getTruckLocations.ts"

import FarmMarker from "./FarmMarker.tsx"
import OverviewPanel from "./OverviewPanel.tsx"
import TruckMarker from "./TruckMarker.tsx"

import type { Farm } from "../../types/logisticsTypes.ts"

export default function LogisticsMap({
  mapRef,
  farm,
  setFarm,
}: {
  mapRef: React.RefObject<MapRef | null>
  farm: number | null
  setFarm: (id: number | null) => void
}) {
  const { effective } = useTheme() // "light" | "dark"
  const isDark = effective === "dark"

  const truckQuery = useQuery({
    queryKey: ["truckLocations"],
    queryFn: getTruckLocations,
    refetchInterval: 5000,
  })

  const farmQuery = useQuery({
    queryKey: ["tripData", { category: "farms" }],
    queryFn: () => getTripData("farms"),
  })

  const initialViewState = {
    longitude: -88.12190535871697,
    latitude: 44.29902078110768,
    zoom: 10.5,
  }

  const handleClear = () => {
    setFarm(null)
    mapRef?.current?.flyTo({
      zoom: initialViewState.zoom,
      center: [initialViewState.longitude, initialViewState.latitude],
      duration: 2000,
    })
  }

  // Recompute the full style when the theme flips
  const mapStyle: StyleSpecification = useMemo(() => {
    const flavor = namedFlavor(isDark ? "dark" : "light")
    return {
      version: 8,
      glyphs: "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf",
      // swap sprite set to match flavor
      sprite: `https://protomaps.github.io/basemaps-assets/sprites/v4/${isDark ? "dark" : "light"}`,
      sources: {
        protomaps: {
          type: "vector",
          url: "https://protomap-test.livelyriver-a7697426.eastus.azurecontainerapps.io/20251013.json",
          attribution:
            '<a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org">OpenStreetMap</a>',
        },
      },
      layers: layers("protomaps", flavor, { lang: "en" }),
    }
  }, [isDark])

  return (
    <div className="col-span-4 md:col-span-2 row-span-2 flex justify-center items-center border rounded-md shadow-sm min-h-[500px]">
      <Map
        key={effective} // optional: force remount to guarantee a clean style swap
        initialViewState={initialViewState}
        ref={mapRef}
        style={{ width: "100%", height: "100%", borderRadius: "5px" }}
        mapStyle={mapStyle}
      >
        {Array.isArray(farmQuery.data) &&
          farmQuery.data?.map((f) => <FarmMarker farm={f as Farm} key={f.name} />)}
        {Array.isArray(truckQuery.data) &&
          truckQuery.data.map((t) => <TruckMarker truck={t} key={t.number} />)}
        <FullscreenControl position="top-right" />
        {farm != null && <OverviewPanel clearFarm={handleClear} farmId={farm} />}
      </Map>
    </div>
  )
}
